<script setup lang="ts">
import { computed } from "vue";
import { Check } from "@lucide/vue";
import { weatherDescription } from "rainowls-shared";
import type { TemperatureUnit } from "@/stores/settings.store";
import type { LocationWeatherSummary } from "@/utils/locationSummary";
import LocationWeatherBadge from "./LocationWeatherBadge.vue";

// The visible strip and its offscreen measurement use this same capsule so
// weather, selection, and unit changes cannot produce different widths.
const props = defineProps<{
  name: string;
  selected: boolean;
  summary?: LocationWeatherSummary;
  loading: boolean;
  unit: TemperatureUnit;
  now: number;
}>();

defineEmits<{ select: [] }>();

const caption = computed(() => {
  if (props.summary) return weatherDescription(props.summary.weatherCode);
  return props.loading ? "Updating…" : "Weather unavailable";
});
</script>

<template>
  <button
    type="button"
    class="inline-flex min-h-[52px] shrink-0 items-center gap-2.5 rounded-full border px-3 py-2 text-left text-text-primary transition-[background-color,border-color,transform] duration-150 hover:bg-app-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none"
    :class="
      selected
        ? 'border-accent bg-app-elevated'
        : 'border-app-border bg-app-surface'
    "
    :aria-pressed="selected"
    @click="$emit('select')"
  >
    <span class="max-w-36 min-w-0">
      <span
        class="block truncate text-sm leading-5 font-medium"
        :title="name"
        >{{ name }}</span
      >
      <span class="block truncate text-[11px] leading-4 text-text-muted">{{
        caption
      }}</span>
    </span>
    <Check
      class="size-3.5 shrink-0 text-accent"
      :class="{ invisible: !selected }"
      aria-hidden="true"
    />
    <LocationWeatherBadge
      :summary="summary"
      :loading="loading"
      :unit="unit"
      :now="now"
    />
  </button>
</template>
